/**
 * Default Spacing Tokens
 *
 * This module defines the default spacing scale for the EroChat Design System.
 * All values use rem units for accessibility and scalability.
 *
 * Base unit: 1rem = 16px (browser default)
 *
 * @module design-system/tokens/spacing
 */

import type { SpacingTokens } from './types';

/**
 * Default spacing scale
 *
 * Based on a 4px (0.25rem) base unit, following a consistent multiplier pattern:
 * - xs: 1x base (4px)
 * - sm: 2x base (8px)
 * - md: 4x base (16px) - Default/base spacing
 * - lg: 6x base (24px)
 * - xl: 8x base (32px)
 * - 2xl: 12x base (48px)
 * - 3xl: 16x base (64px)
 *
 * @example
 * ```tsx
 * import { defaultSpacing } from '@/design-system/tokens/spacing';
 *
 * // Use in inline styles
 * <div style={{ padding: defaultSpacing.md }} />
 *
 * // Use with CSS variables
 * document.documentElement.style.setProperty('--spacing-md', defaultSpacing.md);
 * ```
 */
export const defaultSpacing: SpacingTokens = {
  /** 4px - Minimal spacing for tight layouts */
  xs: '0.25rem',

  /** 8px - Small spacing for compact elements */
  sm: '0.5rem',

  /** 16px - Medium/default spacing */
  md: '1rem',

  /** 24px - Large spacing for sections */
  lg: '1.5rem',

  /** 32px - Extra large spacing */
  xl: '2rem',

  /** 48px - 2x extra large spacing for major sections */
  '2xl': '3rem',

  /** 64px - 3x extra large spacing for page-level separation */
  '3xl': '4rem',
} as const;

/**
 * Spacing scale in pixels (for reference and calculations)
 *
 * Use this when you need pixel values for calculations,
 * but always convert to rem for final CSS output.
 */
export const spacingPx = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

/**
 * Spacing token keys for iteration
 */
export const spacingKeys = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'] as const;

/**
 * Type for spacing token keys
 */
export type SpacingKey = (typeof spacingKeys)[number];

/**
 * CSS variable names for spacing tokens
 *
 * These map to the CSS custom properties that will be injected into the DOM.
 */
export const spacingCSSVariables = {
  xs: '--spacing-xs',
  sm: '--spacing-sm',
  md: '--spacing-md',
  lg: '--spacing-lg',
  xl: '--spacing-xl',
  '2xl': '--spacing-2xl',
  '3xl': '--spacing-3xl',
} as const;

/**
 * Generate CSS variables map for spacing tokens
 *
 * @param spacing - Spacing tokens to convert
 * @returns Object mapping CSS variable names to values
 *
 * @example
 * ```ts
 * const cssVars = getSpacingCSSVariables(defaultSpacing);
 * // { '--spacing-xs': '0.25rem', '--spacing-sm': '0.5rem', ... }
 * ```
 */
export function getSpacingCSSVariables(spacing: SpacingTokens = defaultSpacing): Record<string, string> {
  return {
    '--spacing-xs': spacing.xs,
    '--spacing-sm': spacing.sm,
    '--spacing-md': spacing.md,
    '--spacing-lg': spacing.lg,
    '--spacing-xl': spacing.xl,
    '--spacing-2xl': spacing['2xl'],
    '--spacing-3xl': spacing['3xl'],
  };
}
